import { useEffect, useRef } from 'react'

export type WelcomeDestination = 'parkers' | 'permits' | 'validation' | 'billing' | 'resident'

export interface WelcomeSection {
  tab: WelcomeDestination
  label: string
  description: string
}

export function TenantWelcomeModal({
  tenantName,
  sections,
  onDismiss,
  onSelect,
}: {
  tenantName: string
  sections: WelcomeSection[]
  onDismiss: () => void
  onSelect: (tab: WelcomeDestination) => void
}) {
  const closeRef = useRef<HTMLButtonElement>(null)
  const modalRef = useRef<HTMLElement>(null)
  const dismissRef = useRef(onDismiss)

  useEffect(() => { dismissRef.current = onDismiss }, [onDismiss])

  useEffect(() => {
    const previouslyFocused = document.activeElement instanceof HTMLElement ? document.activeElement : null
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    closeRef.current?.focus()
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        dismissRef.current()
        return
      }
      if (event.key !== 'Tab') return
      const focusable = Array.from(modalRef.current?.querySelectorAll<HTMLElement>('button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])') ?? [])
      if (!focusable.length) return
      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault(); last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault(); first.focus()
      }
    }
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      document.body.style.overflow = previousOverflow
      previouslyFocused?.focus()
    }
  }, [])

  return (
    <div
      className="tenant-welcome-overlay"
      onMouseDown={event => { if (event.target === event.currentTarget) onDismiss() }}
    >
      <section
        ref={modalRef}
        className="tenant-welcome-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="tenant-welcome-title"
        aria-describedby="tenant-welcome-description"
      >
        <button ref={closeRef} type="button" className="tenant-welcome-close" onClick={onDismiss} aria-label="Close welcome message">×</button>

        <div className="tenant-welcome-mark" aria-hidden="true">
          <img src="/parkify-logo-color.svg" alt="" />
        </div>
        <div id="tenant-welcome-title" className="tenant-welcome-eyebrow">Welcome to your tenant portal</div>
        <p id="tenant-welcome-description">
          Everything for <strong>{tenantName}</strong> lives here. Choose an area to get started, or explore at your own pace.
        </p>

        <div className="tenant-welcome-sections" aria-label="Dashboard sections">
          {sections.map(section => (
            <button key={section.tab} type="button" className="tenant-welcome-section" onClick={() => onSelect(section.tab)}>
              <span className="tenant-welcome-icon" aria-hidden="true"><WelcomeIcon tab={section.tab} /></span>
              <span className="tenant-welcome-section-copy">
                <strong>{section.label}</strong>
                <span>{section.description}</span>
              </span>
              <span className="tenant-welcome-arrow" aria-hidden="true">→</span>
            </button>
          ))}
        </div>

        <div className="tenant-welcome-footer">
          <span>You can return to any section from the menu.</span>
          <button type="button" className="tenant-welcome-primary" onClick={onDismiss}>Explore dashboard</button>
        </div>
      </section>
    </div>
  )
}

function WelcomeIcon({ tab }: { tab: WelcomeDestination }) {
  const common = { width: 18, height: 18, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 1.8, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const }
  if (tab === 'parkers') return <svg {...common}><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M19 8v6M22 11h-6" /></svg>
  if (tab === 'permits') return <svg {...common}><rect x="3" y="5" width="18" height="14" rx="2" /><path d="M7 9h7M7 13h10M7 17h5" /></svg>
  if (tab === 'validation') return <svg {...common}><path d="M20 12a8 8 0 1 1-4.9-7.4" /><path d="m9 11 3 3L22 4" /></svg>
  if (tab === 'billing') return <svg {...common}><path d="M6 2h12v20l-3-2-3 2-3-2-3 2V2Z" /><path d="M9 7h6M9 11h6M9 15h3" /></svg>
  return <svg {...common}><path d="M3 21h18M5 21V7l7-4 7 4v14M9 21v-6h6v6" /><path d="M9 9h.01M15 9h.01" /></svg>
}
